/**
 * CUSTOMIZATION: Presidio password policy (MyPresidio).
 * Rules are evaluated live for the requirements checklist and by SpecialValidators.password.
 */
export const PRESIDIO_PASSWORD_MIN_LENGTH = 10;

export type PresidioPasswordRequirementId =
  | 'minLength'
  | 'uppercase'
  | 'lowercase'
  | 'number'
  | 'special'
  | 'noWhitespace';

export interface PresidioPasswordRequirement {
  id: PresidioPasswordRequirementId;
  /** i18n key shown in the live checklist */
  labelKey: string;
  met: boolean;
}

/** Special character = any non-alphanumeric character other than space/tab. */
const SPECIAL_CHAR = /[^A-Za-z0-9 \t]/;
const HAS_UPPER = /[A-Z]/;
const HAS_LOWER = /[a-z]/;
const HAS_NUMBER = /\d/;
const HAS_SPACE_OR_TAB = /[ \t]/;

export function evaluatePresidioPasswordRequirements(value: string | null | undefined): PresidioPasswordRequirement[] {
  const password = value ?? '';
  return [
    {
      id: 'minLength',
      labelKey: 'account.password.requirement.min_length',
      met: password.length >= PRESIDIO_PASSWORD_MIN_LENGTH,
    },
    {
      id: 'uppercase',
      labelKey: 'account.password.requirement.uppercase',
      met: HAS_UPPER.test(password),
    },
    {
      id: 'lowercase',
      labelKey: 'account.password.requirement.lowercase',
      met: HAS_LOWER.test(password),
    },
    {
      id: 'number',
      labelKey: 'account.password.requirement.number',
      met: HAS_NUMBER.test(password),
    },
    {
      id: 'special',
      labelKey: 'account.password.requirement.special',
      met: SPECIAL_CHAR.test(password),
    },
    {
      id: 'noWhitespace',
      labelKey: 'account.password.requirement.no_whitespace',
      met: !HAS_SPACE_OR_TAB.test(password),
    },
  ];
}

export function isPresidioPasswordValid(value: string | null | undefined): boolean {
  const password = value ?? '';
  if (!password.length) {
    return false;
  }
  return evaluatePresidioPasswordRequirements(password).every(rule => rule.met);
}
